import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
    useDisclosure,
    Button,
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalCloseButton,
    ModalBody,
    ModalFooter,
    AlertIcon,
    Alert,
    Icon,
    IconButton,
    Show,
    Text
} from '@chakra-ui/react';

import { AnimalClass, AnimalInterface } from '../../../core/animal';
import AnimalForm from './animalForm';
import { EditIcon } from '@chakra-ui/icons';

export default function AnimalModal(props: {
    as: 'button' | 'variant' | 'icon' | 'text';
    beforeClose?: Function;
    afterClose?: Function;
    data?: AnimalInterface;
    initiallyOpen?: boolean;
}): React.ReactElement {
    const { t } = useTranslation();
    const { isOpen, onOpen, onClose } = useDisclosure({
        defaultIsOpen: props.initiallyOpen || false
    });

    const [saving, setSaving] = useState<boolean>(false);
    const [isEdit, setEdit] = useState<boolean>(false);

    const onSubmit = (
        e: React.FormEvent<HTMLFormElement>,
        animal: AnimalInterface
    ) => {
        props.beforeClose && props.beforeClose(e, animal);
        onClose();
        props.afterClose && props.afterClose(e, animal);
    };

    useEffect(() => {
        setEdit(props.data?.id !== undefined);
    }, [props.data]);

    return (
        <>
            {props.as === 'button' && (
                <Button
                    w='full'
                    size='md'
                    bg={'green.600'}
                    colorScheme='green'
                    onClick={onOpen}
                >
                    {t(isEdit ? 'form.edit' : 'animal.add')}
                </Button>
            )}

            {props.as === 'variant' && (
                <Button
                    size='sm'
                    colorScheme='green'
                    variant='outline'
                    onClick={onOpen}
                >
                    {t(isEdit ? 'form.edit' : 'animal.add')}
                </Button>
            )}

            {props.as === 'text' && (
                <Text
                    w='full'
                    fontSize='md'
                    style={{ fontWeight: 'bold' }}
                    color='gray.500'
                    onClick={onOpen}
                >
                    {t('animal.add')}
                </Text>
            )}

            {props.as === 'icon' && (
                <Show below='md'>
                    <IconButton
                        size={'sm'}
                        variant='outline'
                        aria-label='Edit land'
                        icon={<Icon as={EditIcon} />}
                        onClick={onOpen}
                    />
                </Show>
            )}

            <Modal isOpen={isOpen} onClose={onClose} scrollBehavior='outside'>
                <ModalOverlay />
                <ModalContent>
                    <ModalHeader>
                        {isEdit
                            ? t('modal.editAnimal')
                            : t('modal.createAnimal')}
                    </ModalHeader>
                    {!props.initiallyOpen && <ModalCloseButton />}
                    <ModalBody>
                        {props.initiallyOpen && (
                            <Alert status='info' mb='4'>
                                <AlertIcon />
                                {t('animal.first')}
                            </Alert>
                        )}
                        <AnimalForm
                            data={props.data as AnimalClass}
                            setState={setSaving}
                            onSubmit={onSubmit}
                        />
                    </ModalBody>

                    <ModalFooter>
                        {!props.initiallyOpen && (
                            <Button
                                colorScheme='orange'
                                variant='outline'
                                size='sm'
                                mr={3}
                                onClick={onClose}
                            >
                                {t('form.close')}
                            </Button>
                        )}
                        <Button
                            colorScheme='green'
                            variant='outline'
                            size='sm'
                            type='submit'
                            form='animalForm'
                            isLoading={saving}
                        >
                            {t('form.save')}
                        </Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>
        </>
    );
}
